/**
 * Where a notification takes you when you click it.
 *
 * The bell used to mark a row read and stop. Every notification carries a `link` — the store has had
 * the column since it was created and six services write one — and not one of them was ever
 * followed. So "Invoice Generated" and "Bid Awarded!" announced that something had happened and left
 * you to go and find it, which for a bid means knowing it lives under Bid Workup and which number it
 * is.
 *
 * That is the owner's report, in his words: "We need a notification system when a bid has been sent
 * to a user to sign. The only reason my admin account knew to go sign the bid is because I was the
 * one that wrote it from the estimator account." A bell that cannot take him there is half of it.
 *
 * ITS OWN MODULE, rather than four helpers inside AppHeader, for the same reason `shared/app_links.py`
 * exists on the server: this is a rule about where things live in the app, and a rule about where
 * things live should have one home on each side. It also makes it testable as logic rather than as
 * an assertion about the text of a 700-line component.
 */

/**
 * Two of the three link shapes in the live data did not resolve.
 *
 * The producers are fixed (src/shared/app_links.py), but rows written before that fix are still in
 * everyone's panel — 246 unread on one staging account alone — and rewriting stored rows is a data
 * migration this does not need. So the reader repairs them.
 *
 * THIS MAP HAS AN EXPIRY CONDITION. When no notification older than the producer fix is still being
 * opened, delete it. Leaving it forever turns a temporary shim into a second, competing definition
 * of where a bid lives.
 */
const LEGACY_LINKS = [
  // `/bid-workup/48` — the module is mounted at `/bidworkup/*` and a bid is `/bidworkup/bids/:id`,
  // so this fell through App.jsx's catch-all to /dashboard.
  [/^\/bid-workup\/(.+)$/, (id) => `/bidworkup/bids/${id}`],
  // `/finance/invoices/<uuid>` — the route is `/finance/package/:packageId`, SINGULAR, so this fell
  // through FinanceModule's catch-all to the finance dashboard: the right module, the wrong screen,
  // and no indication which invoice had been meant.
  [/^\/finance\/invoices\/(.+)$/, (id) => `/finance/package/${id}`],
]

/**
 * A path this app owns, and nothing else.
 *
 * `startsWith('/')` ALONE IS NOT ENOUGH: `//evil.test/x` also starts with a slash, and a browser
 * reads it as protocol-relative and leaves the site. These values are written by six different
 * services into a shared table, and "no service would ever write that" is not a property this
 * function can check — so it checks the string instead.
 *
 * Anything else is treated as no link at all: the row still marks read, and the panel stays put.
 */
export function isInAppLink(link) {
  return typeof link === 'string'
    && link.startsWith('/')
    && !link.startsWith('//')
    && !link.startsWith('/\\')   // `/\evil.test` is treated as protocol-relative by some browsers
}

/**
 * The route to navigate to for a notification, or `null` when there is nowhere to go.
 *
 * Returning null rather than a fallback like '/dashboard' is deliberate. A notification with no
 * usable link is one whose author did not say where it points; sending the person to a dashboard
 * they did not ask for, and closing the panel they were reading, is worse than doing nothing.
 */
export function routeForNotification(notification) {
  const link = notification && notification.link
  if (!isInAppLink(link)) return null
  for (const [pattern, rewrite] of LEGACY_LINKS) {
    const m = link.match(pattern)
    if (m) return rewrite(m[1])
  }
  return link
}
